// App-wide styled dialog service — replaces native window.alert / window.confirm.
// Usable from React components AND imperative DOM handlers (AuditSection), since
// it's a module-level promise API backed by a single <DialogHost/> at the root.
//
//   await showAlert("message", { title });               // resolves when dismissed
//   const ok = await showConfirm("message", { title });  // true = confirmed
//   const text = await showPrompt("message", { title }); // string, or null if cancelled

export type DialogTone = "default" | "danger" | "create" | "info";

export interface DialogOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
  messageHtml?: string;  // when set, rendered as HTML instead of the plain message text
}

export interface PromptOptions extends DialogOptions {
  defaultValue?: string;
  placeholder?: string;
}

export interface DialogRequest extends DialogOptions {
  id: number;
  kind: "alert" | "confirm" | "prompt";
  message: string;
  messageHtml?: string;
  defaultValue?: string;
  placeholder?: string;
  resolve: (value: boolean | string | null) => void;
}

type Listener = (reqs: DialogRequest[]) => void;

let queue: DialogRequest[] = [];
let listeners: Listener[] = [];
let nextId = 1;

function emit() { const snap = [...queue]; listeners.forEach(l => l(snap)); }

export function subscribeDialogs(l: Listener): () => void {
  listeners.push(l);
  l([...queue]);
  return () => { listeners = listeners.filter(x => x !== l); };
}

export function resolveDialog(id: number, value: boolean | string | null) {
  const req = queue.find(r => r.id === id);
  if (!req) return;
  queue = queue.filter(r => r.id !== id);
  req.resolve(value);
  emit();
}

export function showAlert(message: string, opts: DialogOptions = {}): Promise<void> {
  return new Promise(resolve => {
    queue.push({ id: nextId++, kind: "alert", message, ...opts, resolve: () => resolve() });
    emit();
  });
}

export function showConfirm(message: string, opts: DialogOptions = {}): Promise<boolean> {
  return new Promise(resolve => {
    queue.push({ id: nextId++, kind: "confirm", message, ...opts, resolve: v => resolve(v === true) });
    emit();
  });
}

export function showPrompt(message: string, opts: PromptOptions = {}): Promise<string | null> {
  return new Promise(resolve => {
    queue.push({
      id: nextId++, kind: "prompt", message, ...opts,
      resolve: v => resolve(typeof v === "string" ? v : null),
    });
    emit();
  });
}
